/* Whether this browser can be asked to install the app, held somewhere that outlives the
   components that care.

   beforeinstallprompt fires once, early, and only once. The install button used to attach
   its listener from inside its own effect, and the nudge only renders that button six
   seconds in, so by the time anything was listening the event had already come and gone.
   The result was a card that said "Put it on your phone" above a single link reading "Not
   now": an offer with no way to accept it, on the one browser where installing definitely
   works.

   So the listener goes on at import instead. The layout imports the nudge, so this runs
   while the first screen is still painting, which is early enough to catch the event, and
   the components read what it caught whenever they get around to mounting.

   Two worlds, as before. Chrome and Edge hand over a real prompt. Safari fires nothing and
   forbids prompting, so the only honest thing is to say where Apple put the control. */

type Prompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let waiting: Prompt | null = null;
let installed = false;
const watchers = new Set<() => void>();

function changed() {
  for (const w of watchers) w();
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export function isApple() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    waiting = e as Prompt;
    changed();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    waiting = null;
    changed();
  });
}

export function watchInstall(fn: () => void) {
  watchers.add(fn);
  return () => void watchers.delete(fn);
}

/** What to offer, if anything. "none" means say nothing at all rather than show a dead button. */
export function installState(): "none" | "prompt" | "ios" | "done" {
  if (installed) return "done";
  if (isStandalone()) return "none";
  if (waiting) return "prompt";
  return isApple() ? "ios" : "none";
}

/** True if it went on the home screen. False covers both "declined" and "nothing to ask". */
export async function install() {
  if (!waiting) return false;
  const asking = waiting;
  waiting = null;
  await asking.prompt();
  const { outcome } = await asking.userChoice;
  if (outcome === "accepted") installed = true;
  changed();
  return outcome === "accepted";
}
